import React from 'react';
import PropTypes from 'prop-types';
import Dropdown from 'relements/build/esm/input/dropdown';
import Text from 'relements/build/esm/input/text';
import Checkbox from 'relements/build/esm/input/checkbox';
import Accordion from 'relements/build/esm/ui/accordion';
import Loader from 'relements/build/esm/decorators/loader';

// Actions
import { createAlert } from '@src/actions/BotActions';

// Constants
import {
  TEMPLATE_TYPES,
  TEMPLATE_CATEGORIES,
  AlertTypes,
} from '@src/constants/MogamboConstants';
import {
  CREATE_TEMPLATE_DESCRIPTION_TOOLTIP,
  CREATE_TEMPLATE_NAME_TOOLTIP,
} from '@src/constants/Tooltips';

// Utils
import connection from '@src/connection';
import * as Utils from '../../_utils';

// Components

import * as maps from './maps';
import './EditTemplateDetailsModal.scss';

@Loader({})
@connection(maps.listenToStores, maps.getStateFromStores)
export default class EditTemplateDetailsModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeIndex: -1,
      templatePurposeValid: true,
      templateNameValid: true,
    };
  }

  componentDidMount() {
    this.props.activateLoader();
    maps.fetchTemplateData(this.props.templateId, (error) => {
      this.props.deactivateLoader();
      if (error) {
        createAlert(error, undefined, AlertTypes.NEGATIVE);
      }
    });
  }

  handleTextInputFocus = (type) => {
    const validKey = `${type}Valid`;
    this.setState({
      [validKey]: true,
    });
  };

  componentWillUnmount() {
    setTimeout(() => {
      maps.updateTemplateData(null, {});
    }, 0);
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.templateData &&
      this.props.loaderActive &&
      this.props.templateId === nextProps.templateData.id
    ) {
      this.isSubstoryTemplate = [
        TEMPLATE_TYPES.SUBSTORY_TEMPLATE,
        TEMPLATE_TYPES.SUBSTORY_WITH_API_TEMPLATE,
      ].includes(nextProps.templateData.template_type);

      this.isGeneralNodeTemplate = [
        TEMPLATE_TYPES.BOT_GENERAL_TEMPLATE,
      ].includes(nextProps.templateData.template_type);
      this.props.deactivateLoader();
      setTimeout(() => {
        maps.updateComponentModal({
          title: nextProps.templateData.name,
          subtitle: 'Edit Smart Skill',
          hasSaveButton: true,
          onSave: this.onSaveClicked,
        });
      }, 0);
    }
  }

  render() {
    return (
      <div className="edit-template-details-modal">
        <div className="edit-template-details-modal-body">
          {this.renderTemplateName()}
          {this.renderTemplateCategorySelector()}
          {this.renderTemplatePurposeDetails()}
          {this.renderTemplateActiveStatus()}
          {this.renderTemplateHowToUseDetails()}
        </div>
        <Accordion>
          <Accordion.Item
            itemIndex={0}
            active={this.state.activeIndex === 0}
            onChange={(activeIndex) => this.setState({ activeIndex })}
            renderHeader={({ ExpandIcon }) => (
              <div className="edit-template-details-accordion-header">
                API(s)
                <ExpandIcon />
              </div>
            )}
          >
            {this.renderTemplateAPIDetails()}
          </Accordion.Item>
          <Accordion.Item
            itemIndex={1}
            onChange={(activeIndex) => this.setState({ activeIndex })}
            active={this.state.activeIndex === 1}
            renderHeader={({ ExpandIcon }) => (
              <div className="edit-template-details-accordion-header">
                Entity(s)
                <ExpandIcon />
              </div>
            )}
          >
            {this.renderTemplateEntityDetails()}
          </Accordion.Item>
        </Accordion>
      </div>
    );
  }

  /**
   * @description Update a template's tag/category.
   * Currently, we allow saving 1 template in 1 category. However, backend supports
   * associating multiple tags to a template.
   * @memberof EditTemplateDetailsModal
   */
  renderTemplateCategorySelector = () => {
    let category = 'Generic';
    if (
      this.props.templateData.tags &&
      this.props.templateData.tags.length > 0
    ) {
      category = this.props.templateData.tags[0];
    }
    return (
      <div className="template-details-container">
        <Dropdown
          withSearch
          label="Category"
          placeholder="Select category"
          className="template-details-input"
          value={{
            title: category,
            value: category,
          }}
          onChange={(val) => this.updateTemplateCategory(val.value)}
          optionKey="title"
          options={TEMPLATE_CATEGORIES}
        />
      </div>
    );
  };

  renderTemplateName = () => {
    const { name } = this.props.templateData;
    const { templateNameValid } = this.state;
    return (
      <div className="template-details-container">
        <Text
          label="Smart Skill Name *"
          className="template-details-input"
          value={name?.trim()}
          error={
            !templateNameValid
              ? 'Smart Skill Name can only contain A-Z, a-z, 0-9, spaces and underscore.'
              : null
          }
          onChange={this.updateTemplateName}
          onFocus={() => this.handleTextInputFocus('templateName')}
          placeholder="Describe the name of this Smart Skill"
          tooltip={CREATE_TEMPLATE_NAME_TOOLTIP.text}
        />
      </div>
    );
  };

  renderTemplatePurposeDetails = () => {
    const { description } = this.props.templateData;
    const { templatePurposeValid } = this.state;
    return (
      <div className="template-details-container">
        <Text
          multiline
          label="Smart Skill's Purpose *"
          className="template-details-input"
          value={description}
          error={
            !templatePurposeValid
              ? "Smart Skill's Purpose cannot be empty."
              : null
          }
          onChange={this.updatePurpose}
          onFocus={() => this.handleTextInputFocus('templatePurpose')}
          placeholder="Describe the purpose of this Smart Skill"
          tooltip={CREATE_TEMPLATE_DESCRIPTION_TOOLTIP.text}
        />
      </div>
    );
  };

  renderTemplateActiveStatus = () => {
    const { active } = this.props.templateData;
    return (
      <div className="template-details-container">
        <Checkbox.Item
          label="Published"
          className="template-details-input"
          value={active}
          onChange={this.updateActiveStatus}
        />
      </div>
    );
  };

  renderTemplateHowToUseDetails = () => {
    const { details = {} } = this.props.templateData;

    return (
      <div className="template-details-container">
        <Text
          multiline
          label="How To Use"
          className="template-details-input"
          placeholder="How To Use"
          value={details.how_to_use}
          onChange={this.updateHowToUse}
        />
      </div>
    );
  };

  renderTemplateAPIDetails = () => {
    if (!this.isSubstoryTemplate) return null;
    const apiDetails = this.props.templateData.details.api || {};
    return (
      <div className="edit-template-details-accordion-body">
        {this.props.templateData.node_api_list.map((nodeApiData) => {
          return (
            <div>
              <Text
                label={`${nodeApiData.athena_name} (${nodeApiData.node_name})`}
                placeholder={`${nodeApiData.athena_name} (${nodeApiData.node_name})`}
                className="template-details-input"
                value={apiDetails[nodeApiData.node_name] || ''}
                onChange={(value) => {
                  this.updateAPIDetails(nodeApiData.node_name, value);
                }}
              />
            </div>
          );
        })}
      </div>
    );
  };

  renderTemplateEntityDetails = () => {
    if (!this.isSubstoryTemplate) return null;
    const entityDetails = this.props.templateData.details.entity || {};
    return (
      <div className="edit-template-details-accordion-body">
        {this.props.templateData.entity_list.map((entityData) => {
          return (
            <Text
              label={entityData.name}
              placeholder={entityData.name}
              className="template-details-input"
              value={entityDetails[entityData.name] || ''}
              onChange={(value) => {
                this.updateEntityDetails(entityData.name, value);
              }}
            />
          );
        })}
      </div>
    );
  };

  updateTemplateName = (value) => {
    const { templateData } = this.props;
    templateData.name = value;
    maps.updateTemplateData(this.props.templateId, templateData);
  };

  updateAPIDetails(nodeName, value) {
    const { templateData } = this.props;
    if (!templateData.details.api) {
      templateData.details.api = {};
    }
    templateData.details.api[nodeName] = value;
    maps.updateTemplateData(this.props.templateId, templateData);
  }

  updateEntityDetails(entityName, value) {
    // Call an action to update the Value corresponding to the entity name
    const { templateData } = this.props;
    if (!templateData.details.entity) {
      templateData.details.entity = {};
    }
    templateData.details.entity[entityName] = value;
    maps.updateTemplateData(this.props.templateId, templateData);
  }

  updatePurpose = (value) => {
    const { templateData } = this.props;
    templateData.description = value;
    maps.updateTemplateData(this.props.templateId, templateData);
  };

  updateTaskMessage = (value) => {
    const { templateData } = this.props;
    templateData.task_message = value;
    maps.updateTemplateData(this.props.templateId, templateData);
  };

  updateActiveStatus = (value) => {
    const { templateData } = this.props;
    templateData.active = value;
    maps.updateTemplateData(this.props.templateId, templateData);
  };

  updateHowToUse = (value) => {
    const { templateData } = this.props;
    templateData.details.how_to_use = value;
    maps.updateTemplateData(this.props.templateId, templateData);
  };

  updateTemplateCategory = (value) => {
    const { templateData } = this.props;
    templateData.tags = [value];
    maps.updateTemplateData(this.props.templateId, templateData);
  };

  onSaveClicked = () => {
    const errorData = Utils.validateTemplateFields(
      'edit',
      this.props.templateData
    );

    if (errorData.hasError) {
      this.setState({
        ...errorData.validFields,
      });

      createAlert(errorData.errorMessage, undefined, AlertTypes.NEGATIVE);
    } else {
      this.props.activateLoader();
      maps.saveTemplateData((error) => {
        this.props.deactivateLoader();
        if (error) {
          createAlert(`${error}`, undefined, AlertTypes.NEGATIVE);
        }
      });
    }
  };
}

EditTemplateDetailsModal.propTypes = {
  templateId: PropTypes.string.isRequired,
  templateData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    active: PropTypes.bool,
    details: PropTypes.object,
    domain_id: PropTypes.string,
    how_to_use: PropTypes.string,
    description: PropTypes.string,
    task_message: PropTypes.string,
    template_type: PropTypes.number,
    tags: PropTypes.arrayOf(PropTypes.string),
    entity_list: PropTypes.arrayOf(PropTypes.object),
    node_api_list: PropTypes.arrayOf(PropTypes.object),
  }),
  activateLoader: PropTypes.func,
  deactivateLoader: PropTypes.func,
  loaderActive: PropTypes.bool,
};
